import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import DefaultAvatar from '../../assets/img/illustrations/default_avatar.png';

/**
 * Drop-in replacement for <img> that handles private S3 URLs.
 * - If src points to our S3 bucket → fetched via /auth/file with Bearer token
 * - Otherwise (base64, local asset, empty, or a public route of our own API such as
 *   /assets/partner-logo/* et /assets/newsletter-image/*) → rendered as-is
 * - If the file is missing or inaccessible → shows fallback silently (no console error)
 */
const S3Image = ({ src, alt = "", className, style, width, height, fallback = DefaultAvatar, ...rest }) => {
    const { FetchBlobAWS } = useApi()
    const [displaySrc, setDisplaySrc] = useState(null)
    const blobUrlRef = useRef(null)

    // Seules les URL S3 passent par /auth/file : cette route fait un GetObject sur le bucket et
    // n'accepte donc qu'une URL S3. Les URL publiques servies par l'API elle-même
    // (/assets/partner-logo/*, /assets/newsletter-image/*) sont chargées directement par le
    // navigateur — elles y repartiraient avec un corps vide, et l'image de repli s'afficherait.
    const isS3Url = useCallback((url) => {
        return typeof url === 'string' && url.startsWith('https://') && url.includes('amazonaws.com')
    }, [])

    useEffect(() => {
        if (!src) {
            setDisplaySrc(fallback)
            return
        }

        if (!isS3Url(src)) {
            setDisplaySrc(src)
            return
        }

        FetchBlobAWS(src).then(blob => {
            if (blob && blob.size > 0) {
                if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current)
                const objectUrl = URL.createObjectURL(blob)
                blobUrlRef.current = objectUrl
                setDisplaySrc(objectUrl)
            } else {
                setDisplaySrc(fallback)
            }
        }).catch(() => {
            setDisplaySrc(fallback)
        })

        return () => {
            if (blobUrlRef.current) {
                URL.revokeObjectURL(blobUrlRef.current)
                blobUrlRef.current = null
            }
        }
    }, [src, FetchBlobAWS, isS3Url, fallback])

    if (!displaySrc) return null

    return (
        <img
            src={displaySrc}
            alt={alt}
            className={className}
            style={style}
            width={width}
            height={height}
            // Les URL chargées directement (route publique, asset local, URL legacy) ne passent pas
            // par le fetch ci-dessus : sans ce garde-fou, une URL morte afficherait l'icône d'image
            // cassée du navigateur au lieu de l'image de repli.
            onError={() => displaySrc !== fallback && setDisplaySrc(fallback)}
            {...rest}
        />
    )
}

export default S3Image;
